import { apiClient, handleApiError } from './api.js';

class MedicalRecordsService {
  // Get all medical records
  async getAllMedicalRecords() {
    try {
      return await apiClient.get('/medicalrecord');
    } catch (error) {
      console.error('Error getting all medical records:', error);
      throw error;
    }
  }

  // Get medical record by ID
  async getMedicalRecordById(recordId) {
    try {
      return await apiClient.get(`/medicalrecord/${recordId}`);
    } catch (error) {
      console.error(`Error getting medical record ${recordId}:`, error);
      throw error;
    }
  }

  // Get medical records by patient ID
  async getMedicalRecordsByPatientId(patientId) {
    try {
      return await apiClient.get(`/medicalrecord/patient/${patientId}`);
    } catch (error) {
      console.error(`Error getting medical records for patient ${patientId}:`, error);
      throw error;
    }
  }

  // Get medical records by physician ID
  async getMedicalRecordsByPhysicianId(physicianId) {
    try {
      return await apiClient.get(`/medicalrecord/doctor/${physicianId}`);
    } catch (error) {
      console.error(`Error getting medical records for physician ${physicianId}:`, error);
      throw error;
    }
  }

  // Get medical records by date range
  async getMedicalRecordsByDateRange(startDate, endDate) {
    try {
      return await apiClient.get(`/medicalrecord/date-range?startDate=${startDate}&endDate=${endDate}`);
    } catch (error) {
      console.error('Error getting medical records by date range:', error);
      throw error;
    }
  }

  // Search medical records
  async searchMedicalRecords(patientId, searchTerm) {
    try {
      return await apiClient.get(`/medicalrecord/search/${patientId}?searchTerm=${searchTerm}`);
    } catch (error) {
      console.error(`Error searching medical records for patient ${patientId}:`, error);
      throw error;
    }
  }

  // Create a new medical record
  async createMedicalRecord(medicalRecord) {
    try {
      // Prepare medical record data with user tracking fields
      const medicalRecordData = {
        ...medicalRecord,
        // Initialize navigation properties for user tracking
        // These will be populated by the backend based on the JWT token
        // Providing empty User objects with UserId property to satisfy model validation
        createdByUser: { userId: 0 },
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.post('/medicalrecord', medicalRecordData);
    } catch (error) {
      console.error('Error creating medical record:', error);
      throw error;
    }
  }

  // Update an existing medical record
  async updateMedicalRecord(recordId, medicalRecord) {
    try {
      // The backend will handle timestamp tracking and user tracking via the JWT token
      // UpdatedBy and UpdatedAt are extracted from the JWT token in the backend
      const medicalRecordData = {
        ...medicalRecord,
        // Initialize navigation property for user tracking
        // This will be populated by the backend based on the JWT token
        // Providing empty User object with UserId property to satisfy model validation
        updatedByUser: { userId: 0 }
      };
      
      return await apiClient.put(`/medicalrecord/${recordId}`, medicalRecordData);
    } catch (error) {
      console.error(`Error updating medical record ${recordId}:`, error);
      throw error;
    }
  }

  // Delete a medical record
  async deleteMedicalRecord(recordId) {
    try {
      return await apiClient.delete(`/medicalrecord/${recordId}`);
    } catch (error) {
      console.error(`Error deleting medical record ${recordId}:`, error);
      throw error;
    }
  }

  // Get billing records associated with a medical record
  async getBillingRecordsByMedicalRecordId(recordId) {
    try {
      return await apiClient.get(`/billing/invoices/medical-record/${recordId}`);
    } catch (error) {
      console.error(`Error getting billing records for medical record ${recordId}:`, error);
      throw handleApiError(error);
    }
  }

  // Link a medical record to a billing record
  async linkMedicalRecordToBilling(medicalRecordId, billingId) {
    try {
      return await apiClient.post(`/billing/invoices/${billingId}/medical-record/${medicalRecordId}`);
    } catch (error) {
      console.error(`Error linking medical record ${medicalRecordId} to billing ${billingId}:`, error);
      throw handleApiError(error);
    }
  }

  // Create a new billing record from a medical record
  async createBillingFromMedicalRecord(medicalRecordId, billingData) {
    try {
      return await apiClient.post(`/billing/invoices/from-medical-record/${medicalRecordId}`, billingData);
    } catch (error) {
      console.error(`Error creating billing from medical record ${medicalRecordId}:`, error);
      throw handleApiError(error);
    }
  }
}

export default new MedicalRecordsService();
